import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Button from '../../../../components/UI/Button';
import Checkbox from '../../../../components/UI/Checkbox';
import NumberInput from '../../../../components/UI/NumberInput';
import TextArea from '../../../../components/UI/TextArea';
import { Heading, Text } from '../../../../components/Typography';
import { getPromptContent } from '../../../../api/promptApi';
import styles from './PromptInput.module.scss';
import { RootState } from '../../../../store';
import {
  setPrompt,
  setModel,
  setAggregationEnabled,
  setAggregationOptions,
} from '../../../../store/summaryTest/slice';
import { SummaryUpdateStatus } from '../../../../socket/subscriptions/summaryUpdateHandler';
import ModelSelector from '../../../../components/ModelSelector';
import { GeminiModel, AIModel } from '../../../../types/AIModels';

const PromptInput: React.FC = () => {
  const dispatch = useDispatch();
  const { prompt, summaryStatus, model, aggregationOptions } = useSelector(
    (state: RootState) => state.summaryTest,
  );

  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const loading = summaryStatus === SummaryUpdateStatus.Loading || templateLoading;

  const loadDefaultTemplate = async () => {
    setTemplateLoading(true);
    setTemplateError(null);

    try {
      const content = await getPromptContent('test-summary');
      dispatch(setPrompt(content));
    } catch (error) {
      console.error('Error loading prompt template:', error);
      setTemplateError('Failed to load prompt template. Please try again.');
    } finally {
      setTemplateLoading(false);
    }
  };

  const clearPrompt = () => {
    dispatch(setPrompt(''));
  };

  const handlePromptChange = (value: string) => {
    dispatch(setPrompt(value));
  };

  const handleModelChange = (selectedModel: AIModel) => {
    dispatch(setModel(selectedModel));
  };

  const selectedModel = model || GeminiModel.Flash;

  const charCount = prompt.length;
  const estimatedTokens = Math.ceil(charCount / 4);
  const isAggregated = aggregationOptions !== null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Heading level={3}>Prompt Template</Heading>
        <div className={styles.controls}>
          <Button variant="secondary" size="small" onClick={loadDefaultTemplate} disabled={loading}>
            {templateLoading ? 'Loading...' : 'Load Template'}
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={clearPrompt}
            disabled={loading || prompt.length === 0}
          >
            Clear
          </Button>
        </div>
      </div>

      <div className={styles.modelSelector}>
        <ModelSelector model={selectedModel} onChange={handleModelChange} />
      </div>

      <div className={styles.templateInfo}>
        <Text weight="semibold">Template Instructions:</Text>
        <Text size="small">Load Template grabs 'test-summary' prompt from the database</Text>
        <Text size="small">
          We'll auto-replace within the prompt {'{{context}}'} with the text from the selected
          sections
        </Text>
        <Text size="small">{'{{date}}'} will be replaced with the current date if needed</Text>
      </div>

      {templateError && (
        <Text color="coral" className={styles.errorContainer}>
          {templateError}
        </Text>
      )}

      {templateLoading && (
        <Text color="neutral-500" className={styles.loadingContainer}>
          Loading prompt template...
        </Text>
      )}

      <div className={styles.promptContainer}>
        <TextArea
          label="Prompt"
          value={prompt}
          onChange={handlePromptChange}
          fullWidth
          rows={15}
          placeholder="Enter your prompt template here, or click 'Load Template' to use the default..."
          disabled={loading}
        />
        <div className={styles.charCount}>
          <Text size="small" color="neutral-500">
            {charCount.toLocaleString()} chars (~{estimatedTokens.toLocaleString()} tokens)
          </Text>
        </div>
      </div>

      <div className={styles.aggregatedContainer}>
        <Checkbox
          label="Aggregated Summary"
          checked={isAggregated}
          onChange={(checked) => dispatch(setAggregationEnabled(checked))}
          disabled={loading}
        />
        {isAggregated && aggregationOptions && (
          <div className={styles.aggregatedOptions}>
            <TextArea
              label="Aggregate Summary Prompt"
              value={aggregationOptions.aggregatePrompt}
              onChange={(value) => dispatch(setAggregationOptions({ aggregatePrompt: value }))}
              fullWidth
              rows={8}
              placeholder="Enter the prompt for the final aggregation step. Use {{context}} to refer to the intermediate summaries."
              disabled={loading}
            />
            <NumberInput
              label="Max Context Tokens per Partial"
              value={aggregationOptions.tokensPerPartial}
              onChange={(value) => dispatch(setAggregationOptions({ tokensPerPartial: value }))}
              placeholder="Not set defaults to context window size"
              disabled={loading}
              min={1000}
              step={1000}
            />
            <Checkbox
              label="Include previous intermediate summary in context of next"
              checked={aggregationOptions.includePreviousOutput}
              onChange={(checked) =>
                dispatch(setAggregationOptions({ includePreviousOutput: checked }))
              }
              disabled={loading}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptInput;
